import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, Calculator, Calendar, FileText, Sparkles, BookOpen, Wand2, LogOut, Radar, Settings, Landmark, ShieldCheck, Upload, Menu, Shield, Eye, Receipt, Flag, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import UnifiedUpload from "@/components/UnifiedUpload";
import Logo from "@/components/Logo";
import { FeedbackModal } from "@/components/FeedbackModal";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";
import VerifyEmailBlockScreen from "@/components/VerifyEmailBlockScreen";

const NAV_ALL = [
  { to: "/app", end: true, icon: LayoutDashboard, label: "Dashboard", tid: "nav-dashboard", show: () => true },
  { to: "/app/verifica", icon: ShieldCheck, label: "Verifica Fiscale", tid: "nav-audit", show: (u) => u?.tipo_piva === "forfettario" || u?.tipo_piva === "ordinario" },
  { to: "/app/bandi", icon: Radar, label: "Radar Bandi", tid: "nav-bandi", showBadge: true, show: (u) => u?.tipo_piva === "forfettario" || u?.tipo_piva === "ordinario" },
  { to: "/app/chat", icon: Sparkles, label: "Copilota AI", tid: "nav-chat", show: () => true },
  { to: "/app/eventi", icon: Receipt, label: "Eventi Fiscali", tid: "nav-eventi", show: () => true },
  { to: "/app/calcolatori", icon: Calculator, label: "Calcolatori", tid: "nav-calc", show: () => true },
  { to: "/app/scadenze", icon: Calendar, label: "Scadenze", tid: "nav-deadlines", show: () => true },
  { to: "/app/archivio", icon: FileText, label: "Archivio", tid: "nav-vault", show: () => true },
  { to: "/app/movimenti", icon: Landmark, label: "Movimenti", tid: "nav-transactions", show: () => true },
  { to: "/app/guide", icon: BookOpen, label: "Guide", tid: "nav-guides", show: () => true },
  { to: "/app/generatore", icon: Wand2, label: "Generatore", tid: "nav-generator", show: (u) => u?.tipo_piva === "forfettario" || u?.tipo_piva === "ordinario" },
  { to: "/app/impostazioni", icon: Settings, label: "Impostazioni", tid: "nav-settings", show: () => true },
];

export default function AppLayout() {
  const { user, realUser, logout, adminViewMode, setAdminViewMode } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    api.get("/bandi/unread-count").then((r) => setUnread(r.data.unread)).catch(() => {});
    const iv = setInterval(() => {
      api.get("/bandi/unread-count").then((r) => setUnread(r.data.unread)).catch(() => {});
    }, 60000);
    return () => clearInterval(iv);
  }, []);

  const doLogout = () => { logout(); navigate("/"); };
  const [mobileOpen, setMobileOpen] = useState(false);

  // Sidebar content estratto per riuso desktop + mobile drawer
  const SidebarContent = ({ onNavigate }) => (
    <>
      <div className="p-6 border-b border-border">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground font-heading font-bold">T</div>
          <div>
            <div className="font-heading font-bold text-xl leading-none">TUOTAX</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">Il tuo copilota fiscale</div>
          </div>
        </div>
        <UnifiedUpload trigger={
          <Button className="w-full rounded-full h-11 shadow-md hover:-translate-y-0.5 transition-transform" data-testid="sidebar-upload-btn">
            <Upload className="w-4 h-4 mr-2" /> Carica documento
          </Button>
        } />
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {NAV_ALL.filter((n) => n.show(user)).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.tid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-accent"
              }`
            }
          >
            <n.icon className="w-4 h-4" />
            <span className="flex-1">{n.label}</span>
            {n.showBadge && unread > 0 && (
              <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary rounded-full h-5 min-w-[20px] px-1.5 text-[10px] font-bold" data-testid="bandi-unread-badge">
                {unread}
              </Badge>
            )}
          </NavLink>
        ))}
        {user?.is_admin && (
          <>
            <NavLink
              to="/app/admin"
              data-testid="nav-admin"
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors mt-2 border-t border-border pt-4 ${
                  isActive ? "bg-secondary text-secondary-foreground" : "text-secondary hover:bg-accent"
                }`
              }
            >
              <Shield className="w-4 h-4" />
              <span className="flex-1">Admin</span>
            </NavLink>
            <NavLink
              to="/app/admin/feedback"
              data-testid="nav-admin-feedback"
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-secondary text-secondary-foreground" : "text-secondary hover:bg-accent"
                }`
              }
            >
              <MessageSquare className="w-4 h-4" />
              <span className="flex-1">Segnalazioni</span>
            </NavLink>
          </>
        )}
        {/* Feature 2: "Segnala un problema" — voce fissa sotto Impostazioni, visibile ovunque */}
        <button
          type="button"
          onClick={() => { setFeedbackOpen(true); onNavigate?.(); }}
          data-testid="nav-feedback"
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <Flag className="w-4 h-4" />
          <span className="flex-1 text-left">Segnala un problema</span>
        </button>
      </nav>
      <div className="p-4 border-t border-border">
        {realUser?.is_admin && (
          <div className="mb-3 rounded-xl border border-secondary/30 bg-secondary/5 p-3" data-testid="admin-view-switcher">
            <div className="flex items-center gap-1.5 mb-2">
              <Eye className="w-3.5 h-3.5 text-secondary" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-secondary">Admin: vista come</span>
            </div>
            <div className="grid grid-cols-2 gap-1">
              {[
                { key: "auto", label: "Auto" },
                { key: "piva", label: "P.IVA" },
                { key: "dipendente", label: "Dipend." },
                { key: "entrambi", label: "Entrambi" },
              ].map((m) => (
                <button
                  key={m.key}
                  onClick={() => setAdminViewMode(m.key)}
                  className={`text-[11px] py-1 px-2 rounded-md font-semibold transition-colors ${
                    adminViewMode === m.key
                      ? "bg-secondary text-secondary-foreground"
                      : "bg-background hover:bg-muted text-muted-foreground"
                  }`}
                  data-testid={`admin-view-${m.key}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {adminViewMode !== "auto" && (
              <div className="text-[10px] text-secondary/80 mt-1.5 italic">⚠️ Preview attivo: la vista non è la tua reale.</div>
            )}
          </div>
        )}
        <div className="mb-3 px-2">
          <div className="text-xs text-muted-foreground">Accesso come</div>
          <div className="text-sm font-semibold truncate" data-testid="user-email">{user?.email}</div>
          {user?.regione && <div className="text-xs text-muted-foreground truncate">{user.regione} · {user.settore || "—"}</div>}
        </div>
        <Button variant="outline" onClick={doLogout} className="w-full rounded-full" data-testid="logout-button">
          <LogOut className="w-4 h-4 mr-2" /> Esci
        </Button>
      </div>
    </>
  );

  // Feature 1: se l'utente non ha verificato e sono passate >48h → blocco totale
  if (user?.is_blocked_by_email) {
    return <VerifyEmailBlockScreen />;
  }

  return (
    <div className="min-h-screen bg-background md:flex">
      {/* Sidebar desktop (>= md) */}
      <aside className="hidden md:flex w-64 border-r border-border bg-card/40 flex-col sticky top-0 h-screen">
        <SidebarContent />
      </aside>

      {/* Topbar mobile con hamburger */}
      <div className="md:hidden sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border flex items-center justify-between px-4 h-14">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-xl" data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0 flex flex-col overflow-y-auto">
            <SheetTitle className="sr-only">Menu di navigazione</SheetTitle>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-heading font-bold text-sm">T</div>
          <div className="font-heading font-bold">TUOTAX</div>
        </div>
        <div className="w-9" /> {/* spacer per centrare il logo */}
      </div>

      <main className="flex-1 min-w-0">
        {/* Feature 1: banner verifica email non invasivo, in cima */}
        <VerifyEmailBanner />
        <div className="max-w-6xl mx-auto p-4 md:p-6 lg:p-10">
          <Outlet />
        </div>
      </main>

      {/* Feature 2: modale segnala problema */}
      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  );
}
